import { getReportURL, ReportActions } from '../../components/projects/generic/ReportGenerationV2/utils';
import { nuc } from '../../utils';
import { get, post, put } from '../api';
import type { Report } from '../../../models/report/types';
import type { DependencyInfoOption, ProjectContext, ProjectGroupContext, ProjectTypes, ReportCategory, ReportContext, ReportFormatType, ReportOptions, ReportURLParams } from '../../components/projects/generic/shared/types';
import type { ReduxAction, SyncReduxAction, Task } from '../../types';

const endpoints = {
  logoSignedUrl: '/api/organization/logo/get_signed_url',
  logo: '/api/organization/logo',
  projects: '/api/projects',
  groups: '/api/project_group',
};

export const actionTypes = Object.freeze({
  RESET: 'REPORT_GENERATION_RESET',
  SET_PROJECT_TYPE: 'REPORT_GENERATION_SET_PROJECT_TYPE',
  SET_PROJECT_CONTEXT: 'REPORT_GENERATION_SET_PROJECT_CONTEXT',
  SET_PROJECT_GROUP_CONTEXT: 'REPORT_GENERATION_SET_PROJECT_GROUP_CONTEXT',
  SET_CATEGORY: 'REPORT_GENERATION_SET_CATEGORY',
  SET_FORMAT: 'REPORT_GENERATION_SET_FORMAT',
  SET_OPTIONS: 'REPORT_GENERATION_SET_OPTIONS',
  FETCH_PUBLIC_REPORT_URL: 'REPORT_GENERATION_FETCH_PUBLIC_REPORT_URL',
  QUEUE_EMAIL_REPORT: 'REPORT_GENERATION_QUEUE_EMAIL_REPORT',
  QUEUE_PUBLISH_REPORT: 'REPORT_GENERATION_QUEUE_PUBLISH_REPORT',
  UPLOAD_LOGO: 'REPORT_GENERATION_UPLOAD_LOGO',
  REMOVE_LOGO: 'REPORT_GENERATION_REMOVE_LOGO',
  SET_CUSTOM_HEADER_TEXT: 'REPORT_GENERATION_SET_CUSTOM_HEADER_TEXT',
  UPDATE_PROJECT_CUSTOM_HEADER_TEXT: 'REPORT_GENERATION_UPDATE_PROJECT_CUSTOM_HEADER_TEXT',
  UPDATE_GROUP_CUSTOM_HEADER_TEXT: 'REPORT_GENERATION_UPDATE_GROUP_CUSTOM_HEADER_TEXT',
  SET_DEPENDENCY_INFO_OPTIONS: 'REPORT_GENERATION_SET_DEPENDENCY_INFO_OPTIONS',
  UPDATE_PROJECT_DEPENDENCY_INFO_OPTIONS: 'REPORT_GENERATION_UPDATE_PROJECT_DEPENDENCY_INFO_OPTIONS',
});

/**
 * Resets non-context state (ie, not category, format, options, etc)
 */
export const resetState = () => ({
  type: actionTypes.RESET,
});

export const setProjectType = (projectType: ProjectTypes): SyncReduxAction<{ projectType: ProjectTypes }> => ({
  type: actionTypes.SET_PROJECT_TYPE,
  projectType,
});

export const setCategory = (category: ReportCategory): SyncReduxAction<{ category: ReportCategory }> => ({
  type: actionTypes.SET_CATEGORY,
  category,
});

export const setFormat = (format: ReportFormatType): SyncReduxAction<
  { format: ReportFormatType }
> => ({
  type: actionTypes.SET_FORMAT,
  format,
});

export const setProjectContext = ({ revision }: ProjectContext): SyncReduxAction<{ revision: string }> => ({
  type: actionTypes.SET_PROJECT_CONTEXT,
  revision,
});

export const setProjectGroupContext = ({ groupId, releaseId }: ProjectGroupContext):
SyncReduxAction<{ groupId: number; releaseId: number }> => ({
  type: actionTypes.SET_PROJECT_GROUP_CONTEXT,
  groupId,
  releaseId,
});

export const setOptions = (options: Partial<ReportOptions>): SyncReduxAction<{ options: ReportOptions }> => ({
  type: actionTypes.SET_OPTIONS,
  options,
});

export const fetchPublicReportURL = (context: ReportContext, options: ReportURLParams):
  ReduxAction<Report> => ({
  type: actionTypes.FETCH_PUBLIC_REPORT_URL,
  payload: async () => get<Report>(getReportURL(ReportActions.Public, context, options)),
});

export const queueEmailReport = (context: ReportContext, options: ReportURLParams): ReduxAction<Task> => ({
  type: actionTypes.QUEUE_EMAIL_REPORT,
  payload: async () => get(getReportURL(ReportActions.Email, context, options)),
});

export const queuePublishReport = (context: ReportContext, options: ReportURLParams): ReduxAction<Task> => ({
  type: actionTypes.QUEUE_PUBLISH_REPORT,
  payload: async () => get(getReportURL(ReportActions.Publish, context, options)),
});

export const uploadLogo = (file: File):
  ReduxAction => ({
  type: actionTypes.UPLOAD_LOGO,
  payload: async () => {
    const { data } = await post<{filename: string; contentType: string}, { signedUrl: string }>(
      endpoints.logoSignedUrl,
      { filename: file.name, contentType: file.type },
    );

    if (!data) {
      throw new Error('Failed to get signed URL');
    }

    // api.put doesn't seem to support blob bodies - fetch does
    await fetch(data.signedUrl, {
      method: 'PUT',
      body: file,
    });

    return post(endpoints.logo, { filename: file.name });
  },
});

export const updateProjectCustomHeaderText = (locator: string, reportCustomText: string):
ReduxAction => ({
  type: actionTypes.UPDATE_PROJECT_CUSTOM_HEADER_TEXT,
  payload: async () => put(`${endpoints.projects}/${nuc(locator)}`, { reportCustomText }),
});

export const updateGroupCustomHeaderText = (groupId: number, reportCustomText: string):
ReduxAction => ({
  type: actionTypes.UPDATE_PROJECT_CUSTOM_HEADER_TEXT,
  payload: async () => put(`${endpoints.groups}/${groupId}`, { reportCustomText }),
});

export const setCustomHeaderText = (customHeaderText: string): SyncReduxAction<{ customHeaderText: string }> => ({
  type: actionTypes.SET_CUSTOM_HEADER_TEXT,
  customHeaderText,
});

export const removeLogo = ():
  ReduxAction => ({
  type: actionTypes.REMOVE_LOGO,
  payload: async () => post(endpoints.logo, { unset: true }),
});

export const setDependencyInfoOptions = (dependencyInfoOptions: DependencyInfoOption[]):
SyncReduxAction<{ dependencyInfoOptions: DependencyInfoOption[] }> => ({
  type: actionTypes.SET_DEPENDENCY_INFO_OPTIONS,
  dependencyInfoOptions,
});

export const updateProjectDependencyInfoOptions = (locator: string, options: DependencyInfoOption[]):
ReduxAction => ({
  type: actionTypes.UPDATE_PROJECT_DEPENDENCY_INFO_OPTIONS,
  payload: async () => put(`${endpoints.projects}/${nuc(locator)}`, { bom_column_settings: options }),
});
